import invariant from 'invariant';
import type { ReactNode } from 'react';

import type { I18nFn } from 'i18n/types/I18nFn';

export type FlashMessageStyle = 'success' | 'info' | 'warn' | 'error';

export function flashMessageTypeMapFn(i18n: I18nFn): Record<
  FlashMessageType['type'],
  (data: FlashMessageType['data']) => {
    message: string | ReactNode;
    style: FlashMessageStyle;
  }
> {
  return {
    SAVED_SEARCH: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Search saved!'),
      style: 'success',
    }),
    UNSAVED_SEARCH: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('You have removed your saved search.'),
      style: 'success',
    }),
    UPDATED_SEARCH: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Saved search updated.'),
      style: 'success',
    }),
    USER_ACCOUNT_DELETED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your account has been deleted.'),
      style: 'success',
    }),
    PAYMENT_SUCCESSFUL: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Thanks! Your payment was successful.'),
      style: 'success',
    }),
    PAYMENT_PENDING_CORP: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n(
          'Thanks! Your payment is now pending. ' +
            'It will be processed when your listing is approved.',
        ),
      style: 'success',
    }),
    PAYMENT_PENDING_ORG: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n(
          'Thanks! Your payment is now pending. ' +
            'It will be processed when your organization is approved.',
        ),
      style: 'success',
    }),
    PAYMENT_METHOD_ADDED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Payment method added.'),
      style: 'success',
    }),
    PAYMENT_METHOD_REMOVED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Payment method removed.'),
      style: 'success',
    }),
    JOB_FAMILY_SAVED: () => ({
      message: i18n('Job family saved'),
      style: 'success',
    }),
    JOB_FAMILY_DELETED: () => ({
      message: i18n('Job family deleted'),
      style: 'success',
    }),
    LOCATION_GROUP_SAVED: () => ({
      message: i18n('Location group saved'),
      style: 'success',
    }),
    LOCATION_GROUP_DELETED: () => ({
      message: i18n('Location group deleted'),
      style: 'success',
    }),
    LOCATION_SAVED: () => ({
      message: i18n('Location saved'),
      style: 'success',
    }),
    LOCATION_DELETED: () => ({
      message: i18n('Location deleted'),
      style: 'success',
    }),
    LANDING_PAGE_SAVED: () => ({
      message: i18n('Landing page saved'),
      style: 'success',
    }),
    LANDING_PAGE_DELETED: () => ({
      message: i18n('Landing page deleted'),
      style: 'success',
    }),
    JOB_TITLE_SAVED: () => ({
      message: i18n('Job title saved'),
      style: 'success',
    }),
    JOB_TITLE_DELETED: () => ({
      message: i18n('Job title deleted'),
      style: 'success',
    }),
    JOB_SYNONYM_SAVED: () => ({
      message: i18n('Job synonym saved'),
      style: 'success',
    }),
    JOB_SYNONYM_DELETED: () => ({
      message: i18n('Job synonym deleted'),
      style: 'success',
    }),
    PAGE_SAVED: () => ({
      message: i18n('Page saved'),
      style: 'success',
    }),
    POST_SAVED: () => ({
      message: i18n('Post saved'),
      style: 'success',
    }),
    PAGE_DELETED: () => ({
      message: i18n('Page deleted'),
      style: 'success',
    }),
    POST_DELETED: () => ({
      message: i18n('Post deleted'),
      style: 'success',
    }),
    CATEGORY_SAVED: () => ({
      message: i18n('Category saved'),
      style: 'success',
    }),
    CATEGORY_DELETED: () => ({
      message: i18n('Category deleted'),
      style: 'success',
    }),
    TAG_SAVED: () => ({
      message: i18n('Tag saved'),
      style: 'success',
    }),
    TAG_DELETED: () => ({
      message: i18n('Tag deleted'),
      style: 'success',
    }),
    SUBSITE_SAVED: () => ({
      message: i18n('Subsite saved'),
      style: 'success',
    }),
    SUBSITE_DELETED: () => ({
      message: i18n('Subsite deleted'),
      style: 'success',
    }),
    SCHEDULED_CONTENT_QUEUE_SAVED: () => ({
      message: i18n('Queue saved'),
      style: 'success',
    }),
    SCHEDULED_CONTENT_QUEUE_DELETED: () => ({
      message: i18n('Queue deleted'),
      style: 'success',
    }),
    SCHEDULED_CONTENT_SAVED: () => ({
      message: i18n('Scheduled content saved'),
      style: 'success',
    }),
    SCHEDULED_CONTENT_DELETED: () => ({
      message: i18n('Scheduled content deleted'),
      style: 'success',
    }),
    REDIRECT_SAVED: () => ({
      message: i18n('Redirect saved'),
      style: 'success',
    }),
    REDIRECT_DELETED: () => ({
      message: i18n('Redirect deleted'),
      style: 'success',
    }),
    CMS_GENERIC_SAVED: () => ({
      message: i18n('Saved'),
      style: 'success',
    }),
    USER_SETTINGS_SAVED: () => ({
      message:
        /* TRANSLATORS: confirmation message, personal settings */
        i18n('Settings saved.'),
      style: 'success',
    }),
    USER_PROFILE_SAVED: () => ({
      message: i18n('Profile saved.'),
      style: 'success',
    }),
    USER_ADDITIONAL_INFO_SETTINGS_SAVED: () => ({
      message:
        /* TRANSLATORS: confirmation message for additional info, personal settings */
        i18n('Additional information saved.'),
      style: 'success',
    }),
    EMAIL_VERIFIED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your email address has been verified.'),
      style: 'success',
    }),
    FORBIDDEN: () => ({
      message:
        /* TRANSLATORS: flash message when attempting to view a page you don't have access to */
        i18n('Forbidden'),
      style: 'error',
    }),
    LOGIN_REQUIRED: () => ({
      message:
        /* TRANSLATORS: flash message when redirected to login */
        i18n('Please log in first.'),
      style: 'warn',
    }),
    LOGGED_OUT: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('You have been logged out.'),
      style: 'success',
    }),
    UNSUBSCRIBE_ALERT: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Unsubscribed from email alert'),
      style: 'success',
    }),
    // oxlint-disable-next-line typescript/no-explicit-any
    OAUTH_LINKED: (data: any) => {
      let type;

      if (!data) {
        type = '';
      } else {
        ({ type } = data);
      }

      return {
        /* TRANSLATORS: confirmation message, linked Facebook/Google */
        message: i18n('Your %(providerType)s account is now linked.', {
          providerType: type,
        }),
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    OAUTH_UNLINKED: (data: any) => {
      let type;

      if (!data) {
        type = '';
      } else {
        ({ type } = data);
      }

      return {
        /* TRANSLATORS: confirmation message, linked Facebook/Google */
        message: i18n('Your %(providerType)s account has been unlinked.', {
          providerType: type,
        }),
        style: 'success',
      };
    },
    PASSWORD_CHANGED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your password has been changed.'),
      style: 'success',
    }),
    LISTING_COPIED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your listing has been copied.'),
      style: 'success',
    }),
    LISTING_SAVED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your listing has been saved.'),
      style: 'success',
    }),
    LISTING_SAVED_PUBLISHED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your listing has been saved and published.'),
      style: 'success',
    }),
    LISTING_DRAFT_SAVED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n("Listing saved! Don't forget to publish it when you're ready."),
      style: 'success',
    }),
    LISTING_PUBLISHED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        `${i18n('Your listing has been published')}.`,
      /* There is an issue with one sky, so append the period after the translated string */
      style: 'success',
    }),
    LISTING_REPOSTED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your listing has been reposted.'),
      style: 'success',
    }),
    LISTING_DELETED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your listing has been deleted.'),
      style: 'success',
    }),
    LISTING_HIDDEN: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your listing has been hidden.'),
      style: 'success',
    }),
    LISTING_UNHIDDEN: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your listing has been unhidden.'),
      style: 'success',
    }),
    ORG_PROFILE_SAVED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your changes have been saved!'),
      style: 'success',
    }),
    ORG_DELETED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your organization has been deleted. Good luck!'),
      style: 'success',
    }),
    // oxlint-disable-next-line typescript/no-explicit-any
    ADMIN_REQUEST_APPROVED: (data: any) => {
      invariant(data?.name, 'missing name');
      return {
        /* TRANSLATORS: confirmation message */
        message: i18n('%(name)s is now an administrator!', { name: data.name }),
        style: 'success',
      };
    },
    ADMIN_REQUEST_REJECTED: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('You have denied this administrator request.'),
      style: 'success',
    }),
    INVALID_ADMIN_REQUEST: () => ({
      /* TRANSLATORS: confirmation message */
      message: i18n(
        'The link you followed is either expired or has already been used.',
      ),
      style: 'warn',
    }),
    // oxlint-disable-next-line typescript/no-explicit-any
    ADMIN_ADDED: (data: any) => {
      let message = '';

      if (data) {
        message =
          /* TRANSLATORS: confirmation message */
          i18n('Thanks! %(name)s is now an administrator!', {
            name: data.name,
          });
      }

      return {
        message,
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    ADMIN_INVITED: (data: any) => {
      let message = '';

      if (data) {
        message =
          /* TRANSLATORS: confirmation message */
          i18n("Thanks! We've sent an invite to %(email)s.", {
            email: data.email,
          });
      }

      return {
        message,
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    OWNER_ADDED: (data: any) => {
      let message = '';

      if (data) {
        message =
          /* TRANSLATORS: confirmation message */
          i18n('Thanks! %(name)s is now an owner!', { name: data.name });
      }

      return {
        message,
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    OWNER_INVITED: (data: any) => {
      let message = '';

      if (data) {
        message =
          /* TRANSLATORS: confirmation message */
          i18n("Thanks! We've sent an invite to %(email)s.", {
            email: data.email,
          });
      }

      return {
        message,
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    OWNER_PROMOTED: (data: any) => {
      let message = '';

      if (data) {
        message =
          /* TRANSLATORS: confirmation message */
          i18n("You've just made %(name)s an owner!", { name: data.name });
      }

      return {
        message,
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    REMOVED_OWNER: (data: any) => {
      let message = '';

      if (data) {
        message =
          /* TRANSLATORS: confirmation message */
          i18n('%(userName)s is no longer an owner for %(orgName)s.', {
            userName: data.userName,
            orgName: data.orgName,
          });
      }

      return {
        message,
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    REMOVED_ADMIN: (data: any) => {
      let message = '';

      if (data) {
        message =
          /* TRANSLATORS: confirmation message */
          i18n('%(userName)s is no longer an administrator for %(orgName)s.', {
            userName: data.userName,
            orgName: data.orgName,
          });
      }

      return {
        message,
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    REMOVED_OWNER_SELF: (data: any) => {
      let message = '';

      if (data) {
        message =
          /* TRANSLATORS: confirmation message */
          i18n("You've removed yourself as an owner for %(orgName)s.", {
            orgName: data.orgName,
          });
      }

      return {
        message,
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    REMOVED_ADMIN_SELF: (data: any) => {
      let message = '';

      if (data) {
        message =
          /* TRANSLATORS: confirmation message */
          i18n("You've removed yourself as an administrator for %(orgName)s.", {
            orgName: data.orgName,
          });
      }

      return {
        message,
        style: 'success',
      };
    },
    // oxlint-disable-next-line typescript/no-explicit-any
    UNSAVE_ORG: (data: any) => {
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      invariant(data && data.orgName, 'Missing orgName');
      const message =
        /* TRANSLATORS: confirmation message */
        i18n(
          'You have unsaved %(orgName)s. You will no longer receive notifications from %(orgName)s.',
          { orgName: data.orgName },
        );
      return {
        message,
        style: 'success',
      };
    },
    SAVED_NOTIFICATION_PREFERENCES: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n('Your notification preferences have been saved.'),
      style: 'success',
    }),
    LISTING_FLAGGED: () => ({
      message: i18n('Thanks for reporting this!'),
      style: 'success',
    }),
    PROMO_CREDIT_GIVEN: () => ({
      message: i18n('Promo credit given.'),
      style: 'success',
    }),
    PROMO_CREDIT_REMOVED: () => ({
      message: i18n('Promo credit removed.'),
      style: 'success',
    }),
    STAFF_PASSWORD_CHANGED: () => ({
      message: i18n('Password changed.'),
      style: 'success',
    }),
    STAFF_LISTING_PROMOTED: () => ({
      message: i18n('Listing promoted.'),
      style: 'success',
    }),
    SUBSCRIBED_SUBSITE: () => ({
      message: i18n('Subscribed!'),
      style: 'success',
    }),
    HERO_SEARCH_DELETED: () => ({
      message: i18n('Search hero deleted'),
      style: 'success',
    }),
    SURVEY_SUBMITTED_TOO_MANY_TIMES: () => ({
      message:
        /* TRANSLATORS: confirmation message */
        i18n(
          `We already have your survey information, but we love your enthusiasm!`,
        ),
      style: 'success',
    }),
    LISTING_APPLICATION_SUBMITTED: () => ({
      message:
        /* TRANSLATORS: application confirmation message */
        i18n('Application submitted successfully.'),
      style: 'success',
    }),
    PROFILE_CREATED: () => ({
      message: i18n('Profile created.'),
      style: 'success',
    }),
    INTEGRATION_REQUESTED: () => ({
      message: i18n('Integration requested'),
      style: 'success',
    }),
    EVENT_RSVPS_EMAILED: () => ({
      message: i18n('Event guests emailed.'),
      style: 'success',
    }),
    EVENT_PENDING_SAVED: () => ({
      message: i18n(
        'Your event is now under review. It will be published automatically once approved by an Idealist team member, usually within one business day.',
      ),
      style: 'success',
    }),
    FILE_CREATED: () => ({ message: i18n('File created'), style: 'success' }),
    FILE_UPDATED: () => ({ message: i18n('File updated'), style: 'success' }),
    INTEGRATION_PARTNER_UPDATED: () => ({
      message: i18n('Integration partner updated'),
      style: 'success',
    }),
    INTEGRATION_PARTNER_API_KEY_CREATED: () => ({
      message: i18n('Partner API key created'),
      style: 'success',
    }),
    INTEGRATION_PARTNER_API_KEY_DELETED: () => ({
      message: i18n('Partner API key deleted'),
      style: 'success',
    }),
    INTEGRATION_PARTNER_CLIENT_CREATED: () => ({
      message: i18n('Integration partner client created'),
      style: 'success',
    }),
    INTEGRATION_PARTNER_CLIENT_UPDATED: () => ({
      message: i18n('Integration partner client updated'),
      style: 'success',
    }),
    INTEGRATION_PARTNER_CLIENT_DELETED: () => ({
      message: i18n('Integration partner client deleted'),
      style: 'success',
    }),
    INTEGRATION_PARTNER_CLIENT_API_KEY_CREATED: () => ({
      message: i18n('Client API key created'),
      style: 'success',
    }),
    INTEGRATION_PARTNER_CLIENT_API_KEY_DELETED: () => ({
      message: i18n('Client API key deleted'),
      style: 'success',
    }),
    CUSTOM: (data: { message: string; style: FlashMessageStyle }) => ({
      message: data?.message || 'Invalid custom flash message',
      style: data?.style,
    }),
  };
}
